import cn from 'classnames'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { useNextSanityImage } from 'next-sanity-image'
import Img from 'next/image'
import * as React from 'react'

import sanityClient from '../sanity'
import { ConceptStep } from '../types/concept'
import imageUrlFor from '../utils/imageUrlFor'

type Props = {
  data: ConceptStep[]
}

// when the top of the trigger hits X% up from the top of the viewport
const getStart = (dur, i) => `top -=${dur * (i + 1) - 20}%`

const ConceptSteps = (props: Props) => {
  const comp: undefined | React.Ref<HTMLElement> = React.useRef()
  const { data } = props

  let scroller

  React.useLayoutEffect(() => {
    let ctx = gsap.context(() => {
      const baseLength = data.length
      const totalDur = (baseLength - 1) * 100
      const offsetDurPerc = (100 / data.length) * (data.length - 1)
      const singleDur = (baseLength * 100) / data.length

      const tl = gsap.timeline({
        ease: 'linear',
      })

      tl.to(
        '.center-scroll',
        {
          y: `-${offsetDurPerc}%`,
          ease: 'linear',
        },
        '<'
      )

      scroller = ScrollTrigger.create({
        trigger: '.js-conceptSteps',
        scrub: true,
        pin: true,
        anticipatePin: 1,
        start: 'top top',
        end: `'+=${totalDur}%'`,
        animation: tl,
        // snap: {
        //   snapTo: 1 / (data.length - 1),
        //   duration: {
        //     min: 0.25,
        //     max: 0.75,
        //   },
        //   delay: 0,
        //   directional: false,
        //   ease: 'power2.inOut',
        // },
      })

      const headlines = gsap.utils.toArray('.js-stepMenuItem')
      const images = gsap.utils.toArray('.js-backgroundImage')
      const textBlocks = gsap.utils.toArray('.js-textBlock')
      const container = document.querySelector('.conceptSteps')
      const integers = document.querySelectorAll('.js-integer')

      headlines.forEach((elem, i) => {
        const textBlock = textBlocks[i]

        const start = getStart(singleDur, i)
        const end = `+=${singleDur}%`

        const tl2 = gsap.timeline({
          scrollTrigger: {
            // trigger: '.js-conceptSteps',
            start,
            end,
            toggleActions: i === headlines.length - 1 ? 'play none play reverse' : 'play reverse play reverse',
          },
        })

        tl2.to(elem as any, {
          duration: 0.2,
          opacity: 1,
          x: 16,
          ease: 'power2.inOut',
        })

        tl2.from(textBlock as any, {
          duration: 0.2,
          opacity: 0,
          y: 20,
          ease: 'power2.inOut',
        })
      })

      images.forEach((elem, i) => {
        const start = getStart(singleDur, i)

        const tl2 = gsap.timeline({
          ease: 'linear',
          scrollTrigger: {
            trigger: '.conceptSteps',
            start,
            toggleActions: i === headlines.length - 1 ? 'play none play reverse' : 'play reverse play reverse',
            // toggleClass: 'HEEEEEEJ',
            onToggle: e => {
              const index = e.direction === 1 ? i : i - 1

              if (index >= 0 && index < data.length) {
                const step = data[index]
                if (step.darkBackground) {
                  container.classList.remove('!bg-white')
                  container.classList.remove('!text-black')
                  integers.forEach(integer => {
                    integer.classList.remove('!from-neutral-400')
                    integer.classList.remove('!to-white')
                  })
                } else {
                  container.classList.add('!bg-white')
                  container.classList.add('!text-black')
                  integers.forEach(integer => {
                    integer.classList.add('!from-neutral-400')
                    integer.classList.add('!to-white')
                  })
                }
              }
            },
          },
        })

        tl2.from(elem as any, {
          duration: 1,
          y: '100%',
          ease: 'power2.inOut',
        })
      })
    }, comp)

    return () => {
      ctx.revert()
    }
  }, [])

  return (
    <section ref={comp} className="conceptSteps relative text-white bg-[#1D1D1B] transition-all ease-out duration-700">
      <div className="js-conceptSteps">
        <div className="center overflow-hidden absolute top-0 left-0 w-full h-screen opacity-30 lg:opacity-100">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 lg:-translate-y-[47%]">
            <div className="relative overflow-hidden w-[100vw] sm:w-96 sm:max-w-3xl h-screen lg:h-[80vh]">
              {data.map((el, i) => {
                const imageProps = useNextSanityImage(sanityClient, el.cover, {
                  imageBuilder: () => imageUrlFor(el.cover).ignoreImageParams().width(1400).fit('fill'),
                }) as any

                return (
                  <Img
                    {...imageProps}
                    key={el._id}
                    // src={imageUrlFor(el.cover).ignoreImageParams().width(1400).fit('fill').url()}
                    className="js-backgroundImage absolute left-0 top-0 object-cover w-full sm:w-96 sm:max-w-3xl h-screen lg:h-[80vh]"
                    alt={`Hvordan Justdrive virker - step ${i + 1}`}
                    placeholder="blur"
                    blurDataURL={imageUrlFor(el.cover).ignoreImageParams().width(100).blur(90).url()}
                  />
                )
              })}
            </div>
          </div>

          <div className="hidden md:grid w-full h-screen grid-cols-2 gap-4 lg:gap-0 place-content-center">
            <div className="col-span-1 relative">
              <p className="js-integer zero text-[120vw] md:text-[46vw] text-right text-transparent bg-clip-text bg-gradient-to-b from-neutral-600 to-neutral-900">
                0
              </p>
            </div>
          </div>

          <div className="center-scroll w-full absolute top-0 left-0">
            {data.map(el => {
              return (
                <div key={el._id} className="w-full h-screen grid grid-cols-3 md:grid-cols-2 gap-0 place-content-center">
                  <div className="col-start-2 md:col-start-2 col-span-2 md:col-span-1">
                    <p className="js-integer text-[180vw] sm:text-[120vw] md:text-[46vw] text-transparent bg-clip-text bg-gradient-to-b from-neutral-600 to-neutral-900">
                      {el.index}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="sides relative h-screen w-full grid grid-cols-12 gap-8 place-content-center px-8">
          <ul className="left hidden lg:block col-span-3 space-y-2 list-none p-0 m-0">
            {data.map((el, i) => (
              <li
                key={el._id}
                className={cn('js-stepMenuItem flex items-center opacity-30 cursor-pointer')}
                onClick={() => {
                  scroller.scroll(window.innerHeight * (i + 1))
                }}
              >
                <p className="w-10">0{el.index}</p>
                <p className="">{el.title}</p>
              </li>
            ))}
          </ul>

          <div className="right flex items-center relative col-span-12 lg:col-start-10 lg:col-span-3 place-content-center">
            {data.map(el => (
              <div key={el._id} className="js-textBlock absolute left-0">
                <h2 className=" text-4xl md:text-3xl">{el.title}</h2>
                <p className="mt-4">{el.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ConceptSteps
